import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useThemeMode } from '../contexts/ThemeContext'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { AppSidebar, CommandBar, useIsDesktop } from 'wss3-forge'
import { NotificationsDropdown } from './NotificationsDropdown'
import {
  Home20Regular,
  Person20Regular,
  Shield20Regular,
  Key20Regular,
  Receipt20Regular,
  History20Regular,
  SignOut20Regular,
  WeatherSunny16Regular,
  WeatherMoon16Regular,
  Apps20Regular,
  ChartMultiple20Regular,
  Document20Regular,
  Clock20Regular,
  PeopleTeam20Regular,
  Building20Regular
} from '@fluentui/react-icons'

export function UserLayout() {
  const { user, signOut } = useAuth()
  const { mode, setMode } = useThemeMode()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const [isWebbaMember, setIsWebbaMember] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [commandBarOpen, setCommandBarOpen] = useState(false)

  useEffect(() => {
    if (user) {
      loadProfile()
    }
  }, [user])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setCommandBarOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const loadProfile = async () => {
    if (!user) return

    // Load profile
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('is_webba_member')
        .eq('user_id', user.id)
        .single()
      setIsWebbaMember(data?.is_webba_member || false)
    } catch (err) {
      console.error('Error loading profile:', err)
    }

    // Load admin status separately
    try {
      const { data, error } = await supabase.rpc('is_admin')
      if (!error) {
        setIsAdmin(data === true)
      }
    } catch (err) {
      console.error('Error checking admin:', err)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const getActiveId = () => {
    if (location.pathname === '/') return 'home'
    if (location.pathname.includes('/account')) return 'account'
    if (location.pathname.includes('/privacy')) return 'privacy'
    if (location.pathname.includes('/security')) return 'security'
    if (location.pathname.includes('/activity')) return 'activity'
    if (location.pathname.includes('/subscriptions')) return 'subscriptions'
    if (location.pathname.includes('/transactions')) return 'transactions'
    if (location.pathname === '/organizations') return 'organizations'
    if (location.pathname.includes('/platform/applications')) return 'platform-applications'
    if (location.pathname.includes('/platform/organizations')) return 'platform-organizations'
    if (location.pathname.includes('/platform/statistics')) return 'platform-statistics'
    if (location.pathname === '/platform/docs') return 'platform-docs'
    if (location.pathname === '/platform/docs/sdk') return 'platform-docs-sdk'
    if (location.pathname === '/platform/docs/integration') return 'platform-docs-integration'
    if (location.pathname === '/platform/docs/api') return 'platform-docs-api'
    if (location.pathname.includes('/users')) return 'users'
    return 'home'
  }

  const handleNavigate = (id: string) => {
    switch (id) {
    case 'home':
      navigate('/')
      break
    case 'account':
      navigate('/account')
      break
    case 'privacy':
      navigate('/privacy')
      break
    case 'security':
      navigate('/security')
      break
    case 'activity':
      navigate('/activity')
      break
    case 'subscriptions':
      navigate('/subscriptions')
      break
    case 'transactions':
      navigate('/transactions')
      break
    case 'organizations':
      navigate('/organizations')
      break
    case 'platform-applications':
      navigate('/platform/applications')
      break
    case 'platform-organizations':
      navigate('/platform/organizations')
      break
    case 'platform-statistics':
      navigate('/platform/statistics')
      break
    case 'platform-docs':
      navigate('/platform/docs')
      break
    case 'platform-docs-sdk':
      navigate('/platform/docs/sdk')
      break
    case 'platform-docs-integration':
      navigate('/platform/docs/integration')
      break
    case 'platform-docs-api':
      navigate('/platform/docs/api')
      break
    case 'users':
      navigate('/users')
      break
    }
  }

  const getSections = () => {
    const sections = [
      {
        items: [
          { id: 'home', label: 'Home', icon: <Home20Regular /> }
        ]
      },
      {
        title: 'Account',
        items: [
          { id: 'account', label: 'Profile', icon: <Person20Regular /> },
          { id: 'privacy', label: 'Privacy', icon: <Shield20Regular /> },
          { id: 'security', label: 'Security', icon: <Key20Regular /> },
          { id: 'activity', label: 'Activity', icon: <Clock20Regular /> }
        ]
      },
      {
        title: 'Billing',
        items: [
          { id: 'subscriptions', label: 'Subscriptions', icon: <Receipt20Regular /> },
          { id: 'transactions', label: 'Transactions', icon: <History20Regular /> }
        ]
      },
      {
        title: 'Enterprise',
        items: [
          { id: 'organizations', label: 'My Organizations', icon: <Building20Regular /> }
        ]
      }
    ]

    if (isWebbaMember) {
      const platformItems: any[] = [
        { id: 'platform-applications', label: 'Applications', icon: <Apps20Regular /> },
        { id: 'platform-organizations', label: 'Organizations', icon: <Building20Regular /> },
        {
          id: 'platform-docs-section',
          label: 'Documentation',
          icon: <Document20Regular />,
          defaultOpen: true,
          children: [
            { id: 'platform-docs', label: 'Accueil' },
            { id: 'platform-docs-sdk', label: 'SDK' },
            { id: 'platform-docs-integration', label: 'Intégration' },
            { id: 'platform-docs-api', label: 'API' }
          ]
        },
        { id: 'platform-statistics', label: 'Statistics', icon: <ChartMultiple20Regular /> }
      ]

      if (isAdmin) {
        platformItems.push({ id: 'users', label: 'Users', icon: <PeopleTeam20Regular /> })
      }

      sections.push({
        title: 'WiD Platform',
        items: platformItems
      })
    }

    // Add sign out to sections for mobile (bottomItems don't render in mobile drawer)
    if (!isDesktop) {
      sections.push({
        items: [
          { id: 'signout', label: 'Sign out', icon: <SignOut20Regular />, onClick: handleSignOut } as any
        ]
      })
    }

    return sections
  }

  const getSearchResults = () => {
    const results = [
      { id: 'home', type: 'page', title: 'Home', icon: <Home20Regular /> },
      { id: 'account', type: 'page', title: 'Profile', icon: <Person20Regular /> },
      { id: 'privacy', type: 'page', title: 'Privacy', icon: <Shield20Regular /> },
      { id: 'security', type: 'page', title: 'Security', icon: <Key20Regular /> },
      { id: 'activity', type: 'page', title: 'Activity', icon: <Clock20Regular /> },
      { id: 'subscriptions', type: 'page', title: 'Subscriptions', icon: <Receipt20Regular /> },
      { id: 'transactions', type: 'page', title: 'Transactions', icon: <History20Regular /> }
    ]

    if (isWebbaMember) {
      results.push(
        { id: 'platform-applications', type: 'page', title: 'Applications', icon: <Apps20Regular /> },
        { id: 'platform-docs', type: 'page', title: 'Documentation', icon: <Document20Regular /> },
        { id: 'platform-docs-sdk', type: 'page', title: 'SDK Documentation', icon: <Document20Regular /> },
        { id: 'platform-docs-integration', type: 'page', title: 'Integration Guide', icon: <Document20Regular /> },
        { id: 'platform-docs-api', type: 'page', title: 'API Reference', icon: <Document20Regular /> },
        { id: 'platform-statistics', type: 'page', title: 'Statistics', icon: <ChartMultiple20Regular /> }
      )
    }

    if (isAdmin) {
      results.push(
        { id: 'users', type: 'page', title: 'User Management', icon: <PeopleTeam20Regular /> }
      )
    }

    return results
  }

  const handleSearch = (query: string) => {
    const allResults = getSearchResults()
    if (!query.trim()) return allResults
    const lowerQuery = query.toLowerCase()
    return allResults.filter(r => r.title.toLowerCase().includes(lowerQuery))
  }

  const handleResultSelect = (result: { id: string }) => {
    handleNavigate(result.id)
    setCommandBarOpen(false)
  }

  // Get current page name for AI context (excluding Platform pages)
  const getCurrentPageName = useCallback(() => {
    const path = location.pathname
    if (path === '/') return 'Home'
    if (path.includes('/account')) return 'Profile'
    if (path.includes('/privacy')) return 'Privacy'
    if (path.includes('/security')) return 'Security'
    if (path.includes('/activity')) return 'Activity'
    if (path.includes('/subscriptions')) return 'Subscriptions'
    if (path.includes('/transactions')) return 'Transactions'
    // Don't expose Platform pages to AI
    return 'Home'
  }, [location.pathname])

  // Handle AI query - returns AIResponse type from wss3-forge
  const handleAiQuery = useCallback(async (query: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          query,
          context: {
            currentPage: getCurrentPageName()
          }
        }
      })

      if (error) throw error

      // Determine response type based on suggested actions
      const hasNavigation = data.suggestedActions?.length > 0
      const firstAction = data.suggestedActions?.[0]

      return {
        type: hasNavigation ? 'navigate' as const : 'info' as const,
        message: data.response,
        data: firstAction ? { target: firstAction.id, label: firstAction.label } : undefined
      }
    } catch (err) {
      console.error('AI query error:', err)
      return {
        type: 'info' as const,
        message: 'Désolé, une erreur est survenue. Réessayez plus tard.'
      }
    }
  }, [getCurrentPageName])

  // Handle AI action (when user clicks on suggested action)
  const handleAiAction = useCallback((response: { type: string; data?: { target?: string } }) => {
    if (response.type === 'navigate' && response.data?.target) {
      handleNavigate(response.data.target)
      setCommandBarOpen(false)
    }
  }, [handleNavigate])

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, height: isDesktop ? '100vh' : 'auto', width: isDesktop ? 'auto' : '100%', zIndex: 100 }}>
        <AppSidebar
          logo={
            <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <svg width="120" height="28" viewBox="0 0 763 182" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: 'var(--text-primary)' }}>
              <g clipPath="url(#clip0_344_77)">
                <path d="M91.7937 90.8659L73.8155 142.238L29.5812 2.45244L29.2196 1.29688H0L55.7429 177.471L56.1123 178.627H91.2435L121.949 90.8659H91.7937Z" fill="currentColor"/>
                <path d="M203.282 1.29688H174.07L118.649 176.473L117.965 178.627H147.169L202.598 3.4508L203.282 1.29688Z" fill="currentColor"/>
                <path d="M745.245 153.637V80.4895C745.245 62.5191 730.624 47.8896 712.661 47.8896H658.074C646.573 47.8896 637.211 57.2522 637.211 68.7608V73.0451H717.865V100.26H682.129C673.23 100.26 665.023 101.714 657.744 104.584C650.268 107.453 644.262 111.942 639.907 117.908C635.363 123.812 633.154 131.217 633.154 140.532C633.154 149.848 635.332 156.978 639.797 163.015C644.105 169.131 650.087 173.911 657.594 177.228C664.764 180.27 672.876 181.811 681.704 181.811C690.532 181.811 697.93 180.64 703.589 178.234C709.674 175.664 714.092 172.676 717.496 168.817C718.714 167.417 719.854 166.034 720.9 164.674C721.89 167.763 723.596 170.42 726.088 172.755C730.058 176.481 735.395 178.368 741.967 178.368H763.027V153.629H745.237L745.245 153.637ZM717.842 124.401C717.535 134.464 714.383 142.38 708.456 147.969C702.379 153.873 694.133 156.868 683.953 156.868C677.027 156.868 671.359 155.28 666.65 152.017C662.814 149.25 660.951 145.296 660.951 139.927C660.951 134.558 662.649 131.138 666.249 128.576C670.581 125.809 676.131 124.401 682.75 124.401H717.857H717.842Z" fill="currentColor"/>
                <path d="M586.61 55.7665L586.578 55.7507C580.093 52.0639 573.057 49.6427 565.66 48.5578C562.657 48.1334 559.466 47.9133 556.164 47.9133C555.881 47.9133 555.614 47.929 555.386 47.9526H536.621C524.948 47.9526 515.444 57.4802 515.444 69.1853V73.4696H551.392C562.453 73.4775 571.139 76.8892 577.963 83.8934C584.81 90.9133 588.276 100.92 588.276 113.648V115.88C588.276 128.914 584.904 138.717 577.963 145.839C571.131 152.859 562.343 156.27 551.109 156.27C539.876 156.27 530.993 152.749 524.224 145.799C517.314 138.889 513.958 129.102 513.958 115.88V0H486.177V178.383H513.345V163.597C516.387 167.323 520.239 170.75 525.058 174.005C532.714 179.193 543.185 181.819 556.164 181.819C567.114 181.819 577.287 179.177 586.39 173.981C595.383 168.84 602.725 161.223 608.212 151.341C613.754 141.531 616.443 130.132 616.443 116.478V113.239C616.443 99.8592 613.746 88.2091 608.432 78.6343C603.087 68.8001 595.949 61.3242 586.602 55.7586L586.61 55.7665Z" fill="currentColor"/>
                <path d="M436.568 55.7665L436.536 55.7507C430.051 52.0639 423.015 49.6427 415.618 48.5578C412.615 48.1334 409.424 47.9133 406.122 47.9133C405.839 47.9133 405.579 47.929 405.344 47.9526H386.579C374.906 47.9526 365.409 57.4802 365.409 69.1853V73.4696H401.35C412.411 73.4775 421.097 76.8892 427.921 83.8934C434.768 90.9133 438.234 100.92 438.234 113.648V115.88C438.234 128.914 434.862 138.717 427.921 145.839C421.089 152.859 412.309 156.27 401.075 156.27C389.842 156.27 380.959 152.749 374.182 145.799C367.272 138.889 363.916 129.11 363.916 115.88V0H336.135V178.383H363.303V163.597C366.337 167.323 370.197 170.743 375.016 174.005C382.68 179.193 393.143 181.819 406.122 181.819C417.072 181.819 427.245 179.177 436.356 173.981C445.349 168.84 452.691 161.223 458.178 151.341C463.72 141.531 466.408 130.124 466.408 116.478V113.239C466.408 99.8592 463.712 88.217 458.398 78.6343C453.052 68.808 445.915 61.3242 436.568 55.7586V55.7665Z" fill="currentColor"/>
                <path d="M288.274 51.8202C279.093 46.6947 268.409 44.1006 256.523 44.1006C244.637 44.1006 233.27 46.8677 223.751 52.3311C214.184 57.8024 206.59 65.6006 201.189 75.5134C195.859 85.3319 193.155 96.9034 193.155 109.913V112.822C193.155 125.659 195.859 137.152 201.189 146.971C206.606 156.907 214.278 164.721 224.01 170.192C233.663 175.593 244.936 178.344 257.53 178.383H281.938C295.137 178.383 305.875 167.645 305.875 154.446V152.788H258.214C247.727 152.788 238.899 149.525 231.981 143.087C226.007 137.6 222.422 130.006 221.298 120.518H317.95V107.469C317.95 94.7731 315.316 83.5396 310.128 74.0749C304.869 64.445 297.519 56.9613 288.274 51.8202ZM232.602 77.8717C238.718 72.4712 246.768 69.7355 256.523 69.7355C266.279 69.7355 273.999 72.4712 280.036 77.8639C285.161 82.4548 288.243 88.4449 289.422 96.1487H222.1C223.971 88.5235 227.5 82.3762 232.594 77.8796L232.602 77.8717Z" fill="currentColor"/>
              </g>
              <defs>
                <clipPath id="clip0_344_77">
                  <rect width="763" height="181.73" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            </div>
          }
          sections={getSections()}
          activeId={getActiveId()}
          onNavigate={handleNavigate}
          showSearch
          searchPlaceholder="Search..."
          onSearchClick={() => setCommandBarOpen(true)}
          bottomItems={isDesktop ? [
            { id: 'signout', label: 'Sign out', icon: <SignOut20Regular />, onClick: handleSignOut, className: 'interactive-nav' } as any
          ] : []}
        />
      </div>
      <main style={{ flex: 1, marginLeft: isDesktop ? 260 : 0, marginTop: isDesktop ? 0 : '60px' }}>
        <Outlet />
      </main>

      {/* Floating notifications button */}
      <div
        style={{
          position: 'fixed',
          top: '1.5rem',
          right: '1.5rem',
          zIndex: 1000
        }}
      >
        <NotificationsDropdown />
      </div>

      {/* Floating theme toggle */}
      <div
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          zIndex: 1000,
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '9999px',
          padding: '4px',
          display: 'flex',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
      >
        <button
          onClick={() => setMode('light')}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: mode === 'light' ? 'var(--brand-primary)' : 'transparent',
            color: mode === 'light' ? 'white' : 'var(--text-muted)',
            transition: 'all 0.2s ease'
          }}
        >
          <WeatherSunny16Regular />
        </button>
        <button
          onClick={() => setMode('dark')}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: mode === 'dark' ? 'var(--brand-primary)' : 'transparent',
            color: mode === 'dark' ? 'white' : 'var(--text-muted)',
            transition: 'all 0.2s ease'
          }}
        >
          <WeatherMoon16Regular />
        </button>
      </div>

      <CommandBar
        open={commandBarOpen}
        onClose={() => setCommandBarOpen(false)}
        placeholder="Search or ask AI..."
        onSearch={handleSearch}
        onResultSelect={handleResultSelect}
        onAiQuery={handleAiQuery}
        onAiAction={handleAiAction}
      />
    </div>
  )
}
